import { supabase } from "@/integrations/supabase/client";

// Types
export type InstallationType = 'fauteuil' | 'tabouret' | 'bureau';
export type ThumbnailStyle = 'bold' | 'classique' | 'custom' | 'none';

export interface UserShootingPreferences {
  id: string;
  user_id: string;
  installation_type: string | null;
  decor_color: string | null;
  decor_color_rgb: string | null;
  thumbnail_style: string | null;
  custom_font_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShootingPreferencesInput {
  installation_type?: InstallationType | null;
  decor_color?: string | null;
  decor_color_rgb?: string | null;
  thumbnail_style?: ThumbnailStyle | null;
  custom_font_url?: string | null;
}

// ============================================
// CRUD Operations
// ============================================

/**
 * Get shooting preferences for a user
 */
export const getShootingPreferences = async (userId: string): Promise<{
  success: boolean;
  data?: UserShootingPreferences | null;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('user_shooting_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching shooting preferences:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Create or update shooting preferences for a user
 */
export const upsertShootingPreferences = async (
  userId: string,
  preferences: ShootingPreferencesInput
): Promise<{
  success: boolean;
  data?: UserShootingPreferences;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('user_shooting_preferences')
      .upsert(
        {
          user_id: userId,
          ...preferences,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      )
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error upserting shooting preferences:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Update installation type
 */
export const updateInstallationType = async (
  userId: string,
  installationType: InstallationType
): Promise<{ success: boolean; error?: string }> => {
  return upsertShootingPreferences(userId, { installation_type: installationType });
};

/**
 * Update decor color
 */
export const updateDecorColor = async (
  userId: string,
  decorColor: string
): Promise<{ success: boolean; error?: string }> => {
  // Validate hex color format
  const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
  if (!hexColorRegex.test(decorColor)) {
    return { success: false, error: 'Invalid color format. Use #RRGGBB format.' };
  }
  
  return upsertShootingPreferences(userId, { decor_color: decorColor });
};

/**
 * Update thumbnail style
 */
export const updateThumbnailStyle = async (
  userId: string,
  thumbnailStyle: ThumbnailStyle
): Promise<{ success: boolean; error?: string }> => {
  return upsertShootingPreferences(userId, { thumbnail_style: thumbnailStyle });
};

const USER_FONTS_BUCKET = 'user-fonts';

/**
 * Upload a custom font file to user-fonts storage and return its public URL.
 */
export const uploadCustomFont = async (
  userId: string,
  file: File
): Promise<{ success: boolean; publicUrl?: string; error?: string }> => {
  try {
    const fileName = `${Date.now()}-${file.name}`;
    const filePath = `${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(USER_FONTS_BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: true });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from(USER_FONTS_BUCKET).getPublicUrl(filePath);
    return { success: true, publicUrl: urlData.publicUrl };
  } catch (error) {
    console.error('Error uploading custom font:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Remove a custom font file from user-fonts storage by its public URL.
 * The URL is expected to contain the path under user-fonts (e.g. from getPublicUrl).
 */
export const removeCustomFontByUrl = async (
  fontUrl: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = new URL(fontUrl);
    const pathParts = url.pathname.split(`/${USER_FONTS_BUCKET}/`);
    if (pathParts.length < 2) {
      return { success: false, error: 'Invalid font URL' };
    }
    const filePath = decodeURIComponent(pathParts[1]);
    const { error } = await supabase.storage.from(USER_FONTS_BUCKET).remove([filePath]);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error removing custom font:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Delete shooting preferences for a user
 */
export const deleteShootingPreferences = async (userId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase
      .from('user_shooting_preferences')
      .delete()
      .eq('user_id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting shooting preferences:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// ============================================
// Admin Operations
// ============================================

/**
 * Get shooting preferences for all users (admin only)
 */
export const getAllShootingPreferences = async (): Promise<{
  success: boolean;
  data?: UserShootingPreferences[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('user_shooting_preferences')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching all shooting preferences:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};
